import { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import confetti from 'canvas-confetti';
import { ArrowLeft, Plus, Check, Trash2, Edit, Share2, X, Filter, Calendar, Tag, Copy, Repeat, Clock, ChevronDown, ArrowUpDown, ArrowUp, ArrowDown, LogOut, Users, Trophy, Search, Pencil, Gift, History, MinusCircle } from 'lucide-react';
import { toast } from 'sonner';
import CreateTaskModal from '@/components/CreateTaskModal';
import ShareModal from '@/components/ShareModal';
import ConfirmModal from '@/components/ConfirmModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function TaskListView() {
  const { listId } = useParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [list, setList] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [user, setUser] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [viewingTask, setViewingTask] = useState(null);
  const [filter, setFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('any');
  const [dateFilterType, setDateFilterType] = useState('created');
  const [sortBy, setSortBy] = useState('priority');
  const [sortOrder, setSortOrder] = useState('desc');
  const [showSortMenu, setShowSortMenu] = useState(false);
  const [showFiltersPanel, setShowFiltersPanel] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [loading, setLoading] = useState(true);
  const [userPoints, setUserPoints] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showUsersModal, setShowUsersModal] = useState(false);
  const [isEditingName, setIsEditingName] = useState(false);
  const [editedName, setEditedName] = useState('');
  // Donation states
  const [showDonateModal, setShowDonateModal] = useState(false);
  const [donationRecipient, setDonationRecipient] = useState(null);
  const [donationAmount, setDonationAmount] = useState(1);
  const [donationLoading, setDonationLoading] = useState(false);
  const [donations, setDonations] = useState([]);
  const [showDonationsHistory, setShowDonationsHistory] = useState(false);
  // Deduct points states (owner only)
  const [showDeductModal, setShowDeductModal] = useState(false);
  const [deductTarget, setDeductTarget] = useState(null);
  const [deductAmount, setDeductAmount] = useState(1);
  const [deductReason, setDeductReason] = useState('');
  const [deductLoading, setDeductLoading] = useState(false);

  useEffect(() => {
    fetchData();
  }, [listId]);

  const fetchData = async () => {
    try {
      const [userRes, listRes, tasksRes, pointsRes] = await Promise.all([
        axios.get(`${API}/auth/me`, { withCredentials: true }),
        axios.get(`${API}/task-lists/${listId}`, { withCredentials: true }),
        axios.get(`${API}/tasks/${listId}`, { withCredentials: true }),
        axios.get(`${API}/task-lists/${listId}/user-points`, { withCredentials: true })
      ]);
      
      // Verify user data is valid
      if (!userRes.data || !userRes.data.user_id) {
        console.log('No valid user session, redirecting to login');
        navigate('/login');
        return;
      }
      
      setUser(userRes.data);
      setList(listRes.data);
      setTasks(tasksRes.data);
      setUserPoints(pointsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      
      // If authentication error, redirect to login
      if (error.response?.status === 401 || error.response?.status === 403 || !error.response) {
        console.log('Authentication error, redirecting to login');
        navigate('/login');
        return;
      }
      
      toast.error(t('messages.errorLoading'));
      navigate('/');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateListName = async () => {
    if (!editedName.trim() || editedName === list.name) {
      setIsEditingName(false);
      return;
    }
    try {
      const response = await axios.put(`${API}/task-lists/${listId}`, { name: editedName.trim() }, { withCredentials: true });
      setList(response.data);
      setIsEditingName(false);
      toast.success(t('messages.listRenamed'));
    } catch (error) {
      console.error('Error updating list name:', error);
      if (error.response?.status === 403) {
        toast.error(t('messages.onlyOwnerCanRename'));
      } else {
        toast.error(t('messages.errorUpdating'));
      }
    }
  };

  const handleCompleteTask = async (taskId) => {
    try {
      await axios.post(`${API}/tasks/${taskId}/complete`, {}, { withCredentials: true });
      fetchData();
      confetti({
        particleCount: 100,
        spread: 70,
        origin: { y: 0.6 }
      });
      toast.success(t('messages.taskCompleted'));
    } catch (error) {
      console.error('Error completing task:', error);
      toast.error(t('messages.errorCompletingTask'));
    }
  };

  const handleDeleteTask = async (taskId) => {
    setConfirmAction({
      type: 'delete',
      title: t('modals.deleteTask'),
      message: t('modals.deleteTaskMessage'),
      action: async () => {
        try {
          await axios.delete(`${API}/tasks/${taskId}`, { withCredentials: true });
          fetchData();
          toast.success(t('messages.taskDeleted'));
        } catch (error) {
          console.error('Error deleting task:', error);
          toast.error(t('messages.errorDeletingTask'));
        }
      }
    });
    setShowConfirmModal(true);
  };

  const handleDeleteList = async () => {
    setConfirmAction({
      type: 'deleteList',
      title: t('modals.deleteList'),
      message: t('modals.deleteTaskListMessage'),
      action: async () => {
        try {
          await axios.delete(`${API}/task-lists/${listId}`, { withCredentials: true });
          toast.success(t('messages.listDeleted'));
          navigate('/');
        } catch (error) {
          console.error('Error deleting list:', error);
          toast.error(t('messages.errorDeletingList'));
        }
      }
    });
    setShowConfirmModal(true);
  };

  const handleUnsubscribe = async () => {
    setConfirmAction({
      type: 'unsubscribe',
      title: t('modals.leaveList'),
      message: t('modals.leaveListMessage'),
      action: async () => {
        try {
          await axios.post(`${API}/task-lists/${listId}/unsubscribe`, {}, { withCredentials: true });
          toast.success(t('messages.leftList'));
          navigate('/');
        } catch (error) {
          console.error('Error unsubscribing from list:', error);
          toast.error(t('messages.errorLeavingList'));
        }
      }
    });
    setShowConfirmModal(true);
  };

  const handleCloneTask = async (taskId) => {
    try {
      await axios.post(`${API}/tasks/${taskId}/clone`, {}, { withCredentials: true });
      fetchData();
      toast.success(t('messages.taskCloned'));
    } catch (error) {
      console.error('Error cloning task:', error);
      toast.error(t('messages.errorCloningTask'));
    }
  };

  const confirmModalAction = async () => {
    if (confirmAction && confirmAction.action) {
      await confirmAction.action();
    }
    setShowConfirmModal(false);
    setConfirmAction(null);
  };

  const getPriorityColor = (priority) => {
    // Gradient from light blue (1) → purple (10)
    const colors = {
      1: 'bg-[#E0F2FE] text-[#0369A1]',   // Light blue
      2: 'bg-[#BAE6FD] text-[#0369A1]',   // Sky blue
      3: 'bg-[#7DD3FC] text-[#18181B]',   // Blue
      4: 'bg-[#93C5FD] text-[#18181B]',   // Blue-indigo
      5: 'bg-[#A5B4FC] text-[#18181B]',   // Indigo-light
      6: 'bg-[#C4B5FD] text-[#18181B]',   // Violet-light
      7: 'bg-[#D8B4FE] text-[#18181B]',   // Purple-light
      8: 'bg-[#C084FC] text-white',       // Purple
      9: 'bg-[#A855F7] text-white',       // Purple-dark
      10: 'bg-[#7C3AED] text-white'       // Violet/Purple intense
    };
    return colors[priority] || colors[1];
  };

  const getLocale = () => {
    const localeMap = { es: 'es-ES', en: 'en-US', gl: 'gl-ES', ca: 'ca-ES', zh: 'zh-CN' };
    return localeMap[i18n.language] || 'es-ES';
  };

  const isOwner = list?.owner_id === user?.user_id;

  // Date filter helper function
  const isInDateRange = (dateString, range) => {
    if (!dateString) return range === 'any';
    
    const date = new Date(dateString);
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const weekAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
    const monthAgo = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000);
    
    switch (range) {
      case 'today':
        return date >= today;
      case 'week':
        return date >= weekAgo;
      case 'month':
        return date >= monthAgo;
      case 'older':
        return date < monthAgo;
      case 'any':
      default:
        return true;
    }
  };

  const getDateFieldValue = (task, fieldType) => {
    switch (fieldType) {
      case 'created':
        return task.created_at;
      case 'modified':
        return task.updated_at || task.created_at;
      case 'completed':
        return task.completed_at;
      case 'due':
        return task.due_date;
      default:
        return task.created_at;
    }
  };

  const filteredTasks = useMemo(() => {
    return tasks.filter(task => {
      // Search filter by name
      if (searchQuery.trim()) {
        const query = searchQuery.toLowerCase().trim();
        if (!task.title.toLowerCase().includes(query)) return false;
      }
      
      // Status filter
      if (filter === 'completed' && !task.completed) return false;
      if (filter === 'pending' && task.completed) return false;
      
      // Date filter
      if (dateFilter !== 'any') {
        const dateValue = getDateFieldValue(task, dateFilterType);
        if (!isInDateRange(dateValue, dateFilter)) return false;
      }
      
      return true;
    });
  }, [tasks, filter, dateFilter, dateFilterType, searchQuery]);

  // Sort the filtered tasks
  const sortedTasks = useMemo(() => {
    const sorted = [...filteredTasks].sort((a, b) => {
      // Completed tasks always go to the end
      if (a.completed && !b.completed) return 1;
      if (!a.completed && b.completed) return -1;
      
      let aValue, bValue;
      
      switch (sortBy) {
        case 'priority':
          aValue = a.priority || 0;
          bValue = b.priority || 0;
          break;
        case 'created':
          aValue = a.created_at ? new Date(a.created_at).getTime() : 0;
          bValue = b.created_at ? new Date(b.created_at).getTime() : 0;
          break;
        case 'completed':
          aValue = a.completed_at ? new Date(a.completed_at).getTime() : 0;
          bValue = b.completed_at ? new Date(b.completed_at).getTime() : 0;
          break;
        case 'modified':
          aValue = a.updated_at ? new Date(a.updated_at).getTime() : (a.created_at ? new Date(a.created_at).getTime() : 0);
          bValue = b.updated_at ? new Date(b.updated_at).getTime() : (b.created_at ? new Date(b.created_at).getTime() : 0);
          break;
        case 'due':
          // Tasks without due date go to the end
          aValue = a.due_date ? new Date(a.due_date).getTime() : (sortOrder === 'asc' ? Infinity : -Infinity);
          bValue = b.due_date ? new Date(b.due_date).getTime() : (sortOrder === 'asc' ? Infinity : -Infinity);
          break;
        default:
          aValue = a.priority || 0;
          bValue = b.priority || 0;
      }
      
      if (sortOrder === 'asc') {
        return aValue - bValue;
      } else {
        return bValue - aValue;
      }
    });
    
    return sorted;
  }, [filteredTasks, sortBy, sortOrder]);

  const getTaskStatus = (task) => {
    if (!task.due_date) return null;
    const dueDate = new Date(task.due_date);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    if (!task.completed && dueDate < today) {
      return 'overdue';
    }
    if (!task.completed && dueDate.toDateString() === today.toDateString()) {
      return 'today';
    }
    return null;
  };

  // Donation functions
  const fetchDonationsHistory = async () => {
    try {
      const res = await axios.get(`${API}/task-lists/${listId}/donations`, { withCredentials: true });
      setDonations(res.data.donations || []);
    } catch (err) {
      console.error('Error fetching donations:', err);
    }
  };

  const openDonateModal = (recipient) => {
    setDonationRecipient(recipient);
    const myPoints = userPoints?.users?.find(u => u.user_id === user?.user_id)?.points || 0;
    const maxDonation = Math.floor(myPoints / 2);
    setDonationAmount(Math.min(1, maxDonation));
    setShowDonateModal(true);
  };

  const handleDonate = async () => {
    if (!donationRecipient || donationAmount < 1) return;
    
    setDonationLoading(true);
    try {
      const res = await axios.post(
        `${API}/task-lists/${listId}/donate`,
        { recipient_user_id: donationRecipient.user_id, points: donationAmount },
        { withCredentials: true }
      );
      
      toast.success(t('donations.success', { points: donationAmount, name: donationRecipient.name }));
      
      // Refresh user points
      const pointsRes = await axios.get(`${API}/task-lists/${listId}/user-points`, { withCredentials: true });
      setUserPoints(pointsRes.data);
      
      // Refresh donations history
      await fetchDonationsHistory();
      
      setShowDonateModal(false);
      setDonationRecipient(null);
      setDonationAmount(1);
    } catch (err) {
      const message = err.response?.data?.detail || t('donations.error');
      toast.error(message);
    } finally {
      setDonationLoading(false);
    }
  };

  const getMyPoints = () => {
    return userPoints?.users?.find(u => u.user_id === user?.user_id)?.points || 0;
  };

  const getMaxDonation = () => {
    return getMyPoints();  // Can donate all points
  };

  // Deduct points functions (owner only)
  const openDeductModal = (targetUser) => {
    setDeductTarget(targetUser);
    setDeductAmount(1);
    setDeductReason('');
    setShowDeductModal(true);
  };

  const handleDeductPoints = async () => {
    if (!deductTarget || deductAmount < 1) return;
    
    setDeductLoading(true);
    try {
      await axios.post(
        `${API}/task-lists/${listId}/deduct-points`,
        { 
          user_id: deductTarget.user_id, 
          points: deductAmount,
          reason: deductReason 
        },
        { withCredentials: true }
      );
      
      toast.success(t('deductions.success', { points: deductAmount, name: deductTarget.name }));
      
      // Refresh user points
      const pointsRes = await axios.get(`${API}/task-lists/${listId}/user-points`, { withCredentials: true });
      setUserPoints(pointsRes.data);
      
      setShowDeductModal(false);
      setDeductTarget(null);
      setDeductAmount(1);
      setDeductReason('');
    } catch (err) {
      const message = err.response?.data?.detail || t('deductions.error');
      toast.error(message);
    } finally {
      setDeductLoading(false);
    }
  };

  const getTargetPoints = () => {
    return deductTarget?.points || 0;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-[#18181B] border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-3 sm:p-4 md:p-6" data-testid="task-list-view">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-4 sm:mb-6">
          <button
            onClick={() => navigate('/')}
            data-testid="back-btn"
            className="mb-3 sm:mb-4 rounded-full border-2 border-[#18181B] dark:border-zinc-600 bg-white dark:bg-zinc-800 px-3 sm:px-4 py-1.5 sm:py-2 text-sm sm:text-base font-bold hover:bg-zinc-100 dark:hover:bg-zinc-700 hover:shadow-[2px_2px_0px_0px_rgba(24,24,27,1)] transition-all flex items-center gap-1.5 sm:gap-2 text-zinc-900 dark:text-white"
          >
            <ArrowLeft className="w-4 h-4" />
            {t('tasks.back')}
          </button>

          <div className="bg-white dark:bg-zinc-800 border-2 border-[#18181B] dark:border-zinc-600 rounded-xl sm:rounded-2xl hard-shadow p-3 sm:p-4">
            {/* Title Row */}
            <div className="flex items-center gap-2 mb-2">
              {isEditingName ? (
                <div className="flex items-center gap-2 flex-1">
                  <input
                    type="text"
                    value={editedName}
                    onChange={(e) => setEditedName(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleUpdateListName();
                      if (e.key === 'Escape') setIsEditingName(false);
                    }}
                    autoFocus
                    className="flex-1 text-xl sm:text-2xl font-bold bg-transparent border-b-2 border-[#18181B] dark:border-zinc-400 focus:outline-none focus:border-[#ADE498] text-zinc-900 dark:text-white"
                    data-testid="edit-list-name-input"
                  />
                  <button
                    onClick={handleUpdateListName}
                    className="p-1.5 rounded-lg bg-[#ADE498] hover:bg-[#9dd889] border-2 border-[#18181B]"
                    data-testid="save-list-name-btn"
                  >
                    <Check className="w-4 h-4 text-[#18181B]" />
                  </button>
                  <button
                    onClick={() => setIsEditingName(false)}
                    className="p-1.5 rounded-lg bg-zinc-200 dark:bg-zinc-600 hover:bg-zinc-300 dark:hover:bg-zinc-500 border-2 border-[#18181B] dark:border-zinc-500"
                  >
                    <X className="w-4 h-4 text-zinc-900 dark:text-white" />
                  </button>
                </div>
              ) : (
                <>
                  <h1 className="text-xl sm:text-2xl font-bold text-zinc-900 dark:text-white" data-testid="list-name">{list?.name}</h1>
                  {isOwner && (
                    <button
                      onClick={() => {
                        setEditedName(list?.name || '');
                        setIsEditingName(true);
                      }}
                      className="p-1 rounded-lg hover:bg-zinc-100 dark:hover:bg-zinc-700 transition-colors"
                      title={t('tasks.renameList')}
                      data-testid="edit-list-name-btn"
                    >
                      <Pencil className="w-4 h-4 text-zinc-500 dark:text-zinc-400" />
                    </button>
                  )}
                </>
              )}
            </div>
            
            {/* Actions Row */}
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                {/* List Type Badge */}
                <span className="text-xs px-2 py-1 bg-zinc-100 dark:bg-zinc-700 rounded-full text-zinc-500 dark:text-zinc-300">
                  {isOwner ? t('tasks.yourList') : t('tasks.sharedList')}
                </span>
                
                {/* Action Buttons */}
                {isOwner ? (
                  <>
                    <button
                      onClick={() => setShowShareModal(true)}
                      data-testid="share-list-btn"
                      className="rounded-full border-2 border-[#18181B] dark:border-zinc-600 bg-white dark:bg-zinc-700 p-1.5 hover:bg-zinc-100 dark:hover:bg-zinc-600 hover:shadow-[2px_2px_0px_0px_rgba(24,24,27,1)] transition-all"
                      title={t('tasks.share')}
                    >
                      <Share2 className="w-4 h-4 text-zinc-900 dark:text-white" />
                    </button>
                    <button
                      onClick={handleDeleteList}
                      data-testid="delete-list-btn"
                      className="rounded-full border-2 border-[#18181B] dark:border-zinc-600 bg-white dark:bg-zinc-700 p-1.5 hover:bg-red-50 dark:hover:bg-red-900/30 hover:shadow-[2px_2px_0px_0px_rgba(24,24,27,1)] transition-all"
                      title={t('modals.deleteList')}
                    >
                      <Trash2 className="w-4 h-4 text-red-600 dark:text-red-400" />
                    </button>
                  </>
                ) : (
                  <button
                    onClick={handleUnsubscribe}
                    data-testid="leave-list-btn"
                    className="rounded-full border-2 border-[#18181B] dark:border-zinc-600 bg-white dark:bg-zinc-700 px-2.5 py-1 hover:bg-red-50 dark:hover:bg-red-900/30 hover:shadow-[2px_2px_0px_0px_rgba(24,24,27,1)] transition-all flex items-center gap-1 text-xs font-medium"
                  >
                    <LogOut className="w-3.5 h-3.5 text-red-600 dark:text-red-400" />
                    <span className="text-red-600 dark:text-red-400">{t('tasks.leaveList')}</span>
                  </button>
                )}
                
                {/* Team Button - Shows modal with all users */}
                {userPoints && userPoints.users && userPoints.users.length > 1 && (
                  <button
                    onClick={() => setShowUsersModal(true)}
                    data-testid="show-users-btn"
                    className="rounded-full border-2 border-[#18181B] dark:border-zinc-600 bg-white dark:bg-zinc-700 px-2.5 py-1 hover:bg-zinc-100 dark:hover:bg-zinc-600 hover:shadow-[2px_2px_0px_0px_rgba(24,24,27,1)] transition-all flex items-center gap-1.5 text-xs font-medium text-zinc-900 dark:text-white"
                  >
                    <Users className="w-3.5 h-3.5" />
                    <span>{userPoints.users.length}</span>
                  </button>
                )}
              </div>
              
              {/* Your Points Badge - Prominent */}
              {userPoints && (
                <div className="flex items-center gap-2 px-3 py-1.5 bg-[#FFC247] border-2 border-[#18181B] rounded-full shadow-[2px_2px_0px_0px_rgba(24,24,27,1)]">
                  <Trophy className="w-4 h-4 text-[#18181B]" />
                  <span className="text-sm font-bold text-[#18181B]">
                    {userPoints.users?.find(u => u.user_id === user?.user_id)?.points || 0} pts
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Search Box */}
        <div className="relative mb-3">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-zinc-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={t('tasks.searchPlaceholder')}
            data-testid="search-tasks-input"
            className="w-full pl-10 pr-10 py-2 rounded-full border-2 border-[#18181B] dark:border-zinc-600 bg-white dark:bg-zinc-800 text-sm focus:outline-none focus:ring-2 focus:ring-[#ADE498] focus:border-[#18181B] dark:text-white dark:placeholder-zinc-400"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-zinc-400 hover:text-zinc-600"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        {/* Filters Row - Simplified with 2 buttons */}
        <div className="flex items-center gap-2 mb-3 sm:mb-4">
          {/* Filters Button */}
          <div className="relative">
            <button
              onClick={() => setShowFiltersPanel(!showFiltersPanel)}
              data-testid="filters-btn"
              className={`rounded-full border-2 border-[#18181B] dark:border-zinc-600 px-3 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm font-bold transition-all flex items-center gap-1.5 whitespace-nowrap ${
                (filter !== 'all' || dateFilter !== 'any') ? 'bg-[#18181B] text-white' : 'bg-white dark:bg-zinc-800 text-zinc-900 dark:text-white hover:bg-zinc-100 dark:hover:bg-zinc-700'
              }`}
            >
              <Filter className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
              {t('tasks.filters')}
              {(filter !== 'all' || dateFilter !== 'any') && (
                <span className="bg-[#FFC247] text-[#18181B] text-[10px] font-bold px-1.5 py-0.5 rounded-full ml-1">
                  {(filter !== 'all' ? 1 : 0) + (dateFilter !== 'any' ? 1 : 0)}
                </span>
              )}
              <ChevronDown className={`w-3 h-3 transition-transform ${showFiltersPanel ? 'rotate-180' : ''}`} />
            </button>
            
            {/* Filters Panel */}
            {showFiltersPanel && (
              <>
                <div 
                  className="fixed inset-0 z-10" 
                  onClick={() => setShowFiltersPanel(false)}
                />
                <div className="absolute top-full left-0 mt-2 bg-white dark:bg-zinc-800 border-2 border-[#18181B] dark:border-zinc-600 rounded-xl shadow-lg z-20 p-4 min-w-[280px]">
                  {/* Status Filter */}
                  <div className="mb-4">
                    <p className="text-xs font-bold text-zinc-500 dark:text-zinc-400 mb-2">{t('tasks.status')}</p>
                    <div className="flex flex-wrap gap-1.5">
                      {[
                        { value: 'all', label: t('tasks.all'), count: tasks.length },
                        { value: 'pending', label: t('tasks.pendingFilter'), count: tasks.filter(t => !t.completed).length },
                        { value: 'completed', label: t('tasks.completedFilter'), count: tasks.filter(t => t.completed).length }
                      ].map((option) => (
                        <button
                          key={option.value}
                          onClick={() => setFilter(option.value)}
                          data-testid={`filter-${option.value}`}
                          className={`rounded-full border-2 border-[#18181B] dark:border-zinc-600 px-3 py-1.5 text-xs font-bold transition-all ${
                            filter === option.value
                              ? 'bg-zinc-200 dark:bg-zinc-600 text-zinc-900 dark:text-white shadow-[2px_2px_0px_0px_rgba(24,24,27,1)]'
                              : 'bg-white dark:bg-zinc-700 text-zinc-900 dark:text-white hover:bg-zinc-100 dark:hover:bg-zinc-600'
                          }`}
                        >
                          {option.label} ({option.count})
                        </button>
                      ))}
                    </div>
                  </div>
                  
                  {/* Date Filter Type */}
                  <div className="mb-4">
                    <p className="text-xs font-bold text-zinc-500 dark:text-zinc-400 mb-2">{t('tasks.filterByDate')}</p>
                    <div className="flex flex-wrap gap-1.5">
                      {['created', 'modified', 'completed', 'due'].map((type) => (
                        <button
                          key={type}
                          onClick={() => setDateFilterType(type)}
                          className={`rounded-full border-2 border-[#18181B] dark:border-zinc-600 px-3 py-1.5 text-xs font-bold transition-all ${
                            dateFilterType === type
                              ? 'bg-zinc-200 dark:bg-zinc-600 text-zinc-900 dark:text-white shadow-[2px_2px_0px_0px_rgba(24,24,27,1)]'
                              : 'bg-white dark:bg-zinc-700 text-zinc-900 dark:text-white hover:bg-zinc-100 dark:hover:bg-zinc-600'
                          }`}
                        >
                          {type === 'created' && t('tasks.filterByCreated')}
                          {type === 'modified' && t('tasks.filterByModified')}
                          {type === 'completed' && t('tasks.filterByCompleted')}
                          {type === 'due' && t('tasks.filterByDue')}
                        </button>
                      ))}
                    </div>
                  </div>
                  
                  {/* Date Range */}
                  <div className="mb-4">
                    <p className="text-xs font-bold text-zinc-500 dark:text-zinc-400 mb-2">{t('tasks.dateRange')}</p>
                    <div className="flex flex-wrap gap-1.5">
                      {['any', 'today', 'week', 'month'].map((range) => (
                        <button
                          key={range}
                          onClick={() => setDateFilter(range)}
                          data-testid={`date-filter-${range}`}
                          className={`rounded-full border-2 border-[#18181B] dark:border-zinc-600 px-3 py-1.5 text-xs font-bold transition-all ${
                            dateFilter === range
                              ? 'bg-zinc-200 dark:bg-zinc-600 text-zinc-900 dark:text-white shadow-[2px_2px_0px_0px_rgba(24,24,27,1)]'
                              : 'bg-white dark:bg-zinc-700 text-zinc-900 dark:text-white hover:bg-zinc-100 dark:hover:bg-zinc-600'
                          }`}
                        >
                          {range === 'any' && t('tasks.anyDate')}
                          {range === 'today' && t('tasks.today')}
                          {range === 'week' && t('tasks.thisWeek')}
                          {range === 'month' && t('tasks.thisMonth')}
                        </button>
                      ))}
                    </div>
                  </div>
                  
                  {/* Clear Filters */}
                  {(filter !== 'all' || dateFilter !== 'any') && (
                    <button
                      onClick={() => {
                        setFilter('all');
                        setDateFilter('any');
                        setDateFilterType('created');
                      }}
                      className="w-full text-center text-xs font-bold text-zinc-500 dark:text-zinc-400 hover:text-[#18181B] dark:hover:text-white py-2 border-t border-zinc-200 dark:border-zinc-700"
                    >
                      {t('tasks.clearFilters')}
                    </button>
                  )}
                </div>
              </>
            )}
          </div>

          {/* Sort dropdown */}
          <div className="relative">
            <button
              onClick={() => setShowSortMenu(!showSortMenu)}
              data-testid="sort-dropdown"
              className="rounded-full border-2 border-[#18181B] dark:border-zinc-600 px-3 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm font-bold bg-white dark:bg-zinc-800 text-zinc-900 dark:text-white hover:bg-zinc-100 dark:hover:bg-zinc-700 transition-all flex items-center gap-1.5 whitespace-nowrap"
            >
              <ArrowUpDown className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
              {sortBy === 'priority' && t('tasks.sortPriority')}
              {sortBy === 'created' && t('tasks.sortCreated')}
              {sortBy === 'completed' && t('tasks.sortCompleted')}
              {sortBy === 'modified' && t('tasks.sortModified')}
              {sortBy === 'due' && t('tasks.sortDue')}
              <ChevronDown className={`w-3 h-3 transition-transform ${showSortMenu ? 'rotate-180' : ''}`} />
            </button>
            {showSortMenu && (
              <>
                <div 
                  className="fixed inset-0 z-10" 
                  onClick={() => setShowSortMenu(false)}
                />
                <div className="absolute top-full left-0 mt-1 bg-white dark:bg-zinc-800 border-2 border-[#18181B] dark:border-zinc-600 rounded-lg shadow-lg z-20 min-w-[160px]">
                  {['priority', 'created', 'completed', 'modified', 'due'].map((sortOption) => (
                    <button
                      key={sortOption}
                      onClick={() => {
                        setSortBy(sortOption);
                        setShowSortMenu(false);
                      }}
                      data-testid={`sort-${sortOption}`}
                      className={`w-full px-3 py-2 text-left text-xs sm:text-sm hover:bg-zinc-100 dark:hover:bg-zinc-700 first:rounded-t-lg last:rounded-b-lg text-zinc-900 dark:text-white ${
                        sortBy === sortOption ? 'bg-zinc-100 dark:bg-zinc-700 font-bold' : ''
                      }`}
                    >
                      {sortOption === 'priority' && t('tasks.sortPriority')}
                      {sortOption === 'created' && t('tasks.sortCreated')}
                      {sortOption === 'completed' && t('tasks.sortCompleted')}
                      {sortOption === 'modified' && t('tasks.sortModified')}
                      {sortOption === 'due' && t('tasks.sortDue')}
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>

          {/* Sort order toggle */}
          <button
            onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
            data-testid="sort-order-toggle"
            className="rounded-full border-2 border-[#18181B] dark:border-zinc-600 p-1.5 sm:p-2 bg-white dark:bg-zinc-800 text-zinc-900 dark:text-white hover:bg-zinc-100 dark:hover:bg-zinc-700 transition-all"
            title={sortOrder === 'asc' ? t('tasks.sortAsc') : t('tasks.sortDesc')}
          >
            {sortOrder === 'asc' ? (
              <ArrowUp className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
            ) : (
              <ArrowDown className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
            )}
          </button>
        </div>

        {/* Add Task Button */}
        <button
          onClick={() => {
            setEditingTask(null);
            setShowCreateModal(true);
          }}
          data-testid="add-task-btn"
          className="w-full mb-4 sm:mb-6 rounded-full border-2 border-[#18181B] bg-[#67E8F9] px-4 sm:px-6 py-2.5 sm:py-3 text-sm sm:text-base font-bold hover:bg-[#A5F3FC] hover:shadow-[2px_2px_0px_0px_rgba(24,24,27,1)] active:translate-y-1 active:shadow-none transition-all flex items-center justify-center gap-2"
        >
          <Plus className="w-4 h-4 sm:w-5 sm:h-5" />
          {t('tasks.addTask')}
        </button>

        {/* Tasks List */}
        <div className="space-y-2 sm:space-y-3">
          {sortedTasks.map((task) => {
            const taskStatus = getTaskStatus(task);
            
            return (
              <div
                key={task.task_id}
                data-testid={`task-${task.task_id}`}
                className={`bg-white dark:bg-zinc-800 border-2 border-[#18181B] dark:border-zinc-600 rounded-lg sm:rounded-xl hard-shadow-sm p-3 sm:p-4 transition-all ${
                  task.completed ? 'opacity-50' : ''
                } ${taskStatus === 'overdue' ? 'border-red-500' : ''}`}
              >
                <div className="flex items-start gap-2 sm:gap-4">
                  {/* Priority Badge */}
                  <div className={`flex-shrink-0 w-10 h-10 sm:w-12 sm:h-12 rounded-lg sm:rounded-xl border-2 border-[#18181B] dark:border-zinc-600 ${getPriorityColor(task.priority)} flex items-center justify-center font-mono font-bold text-base sm:text-lg`}>
                    {task.priority}
                  </div>

                  {/* Task Content - clickeable to view details */}
                  <div 
                    className="flex-1 min-w-0 cursor-pointer hover:opacity-80 transition-opacity"
                    onClick={() => setViewingTask(task)}
                  >
                    <div className="flex flex-wrap items-center gap-1.5 sm:gap-2 mb-1">
                      <h3 className={`font-bold text-sm sm:text-lg break-words text-zinc-900 dark:text-white ${
                        task.completed ? 'line-through text-zinc-400 dark:text-zinc-500' : ''
                      }`}>
                        {task.title}
                      </h3>
                      {task.recurrent && (
                        <div className="flex items-center gap-0.5 sm:gap-1 px-1.5 sm:px-2 py-0.5 sm:py-1 bg-[#FF8BA7]/20 border border-[#FF8BA7] rounded text-[10px] sm:text-xs font-bold text-[#18181B] dark:text-[#FF8BA7]" title={t('tasks.recurrent')}>
                          <Repeat className="w-2.5 h-2.5 sm:w-3 sm:h-3" />
                          <span className="hidden xs:inline">{t('tasks.recurrent')}</span>
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm flex-wrap">
                      <span className="px-1.5 sm:px-2 py-0.5 bg-[#FFC247]/20 dark:bg-[#FFC247]/30 text-[#18181B] dark:text-[#FFC247] rounded text-xs font-mono font-bold">
                        +{task.points} {t('tasks.pts')}
                      </span>
                      {task.due_date && (
                        <span className={`px-1.5 sm:px-2 py-0.5 rounded flex items-center gap-0.5 sm:gap-1 text-[10px] sm:text-xs ${
                          taskStatus === 'overdue' ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400' :
                          taskStatus === 'today' ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400' :
                          'bg-zinc-100 dark:bg-zinc-700 text-zinc-600 dark:text-zinc-300'
                        }`}>
                          <Calendar className="w-2.5 h-2.5 sm:w-3 sm:h-3" />
                          {new Date(task.due_date).toLocaleDateString(getLocale(), { day: 'numeric', month: 'short' })}
                        </span>
                      )}
                      {task.tags && task.tags.length > 0 && (
                        <div className="flex gap-1 flex-wrap">
                          {task.tags.slice(0, 2).map((tag, idx) => (
                            <span key={idx} className="px-1.5 py-0.5 bg-[#ADE498]/30 text-[#18181B] dark:text-[#ADE498] rounded text-[10px] sm:text-xs flex items-center gap-0.5">
                              <Tag className="w-2.5 h-2.5 sm:w-3 sm:h-3" />
                              {tag}
                            </span>
                          ))}
                          {task.tags.length > 2 && (
                            <span className="px-1.5 py-0.5 bg-zinc-100 dark:bg-zinc-700 text-zinc-600 dark:text-zinc-300 rounded text-[10px] sm:text-xs">
                              +{task.tags.length - 2}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-0.5 sm:gap-2 flex-shrink-0">
                    {/* Clone button - always visible */}
                    <button
                      onClick={() => handleCloneTask(task.task_id)}
                      data-testid={`clone-task-${task.task_id}`}
                      className="p-1.5 sm:p-2 rounded-lg hover:bg-[#67E8F9]/30 transition-colors"
                      title={t('tasks.cloneTask')}
                    >
                      <Copy className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-[#67E8F9]" />
                    </button>
                    {/* Edit button - only for pending tasks */}
                    {!task.completed && (
                      <button
                        onClick={() => {
                          setEditingTask(task);
                          setShowCreateModal(true);
                        }}
                        data-testid={`edit-task-${task.task_id}`}
                        className="p-1.5 sm:p-2 rounded-lg hover:bg-zinc-100 dark:hover:bg-zinc-700 transition-colors hidden sm:block"
                      >
                        <Edit className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-zinc-600 dark:text-zinc-300" />
                      </button>
                    )}
                    {/* Complete button - only clickeable for pending tasks */}
                    {!task.completed ? (
                      <button
                        onClick={() => handleCompleteTask(task.task_id)}
                        data-testid={`complete-task-${task.task_id}`}
                        className="p-1.5 sm:p-2 rounded-lg hover:bg-zinc-100 transition-colors"
                      >
                        <Check className="w-4 h-4 sm:w-5 sm:h-5" />
                      </button>
                    ) : (
                      <div
                        data-testid={`completed-task-${task.task_id}`}
                        className="p-1.5 sm:p-2 rounded-lg bg-[#ADE498] text-[#18181B] cursor-default"
                        title={t('tasks.completedFilter')}
                      >
                        <Check className="w-4 h-4 sm:w-5 sm:h-5" />
                      </div>
                    )}
                    {/* Delete button - only for owner */}
                    {isOwner && (
                      <button
                        onClick={() => handleDeleteTask(task.task_id)}
                        data-testid={`delete-task-${task.task_id}`}
                        className="p-1.5 sm:p-2 rounded-lg hover:bg-red-50 transition-colors"
                      >
                        <Trash2 className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-red-600" />
                      </button>
                    )}
                  </div>
                </div>
                {/* Mobile edit button - only for pending tasks */}
                {!task.completed && (
                  <button
                    onClick={() => {
                      setEditingTask(task);
                      setShowCreateModal(true);
                    }}
                    className="mt-2 w-full py-1.5 text-xs font-bold text-zinc-500 hover:text-zinc-700 hover:bg-zinc-50 rounded-lg transition-colors sm:hidden flex items-center justify-center gap-1"
                  >
                    <Edit className="w-3 h-3" />
                    {t('tasks.editTask')}
                  </button>
                )}
              </div>
            );
          })}

          {sortedTasks.length === 0 && (
            <div className="text-center py-8 sm:py-12 text-zinc-400">
              <p className="text-sm sm:text-base">
                {dateFilter !== 'any' ? t('tasks.noTasksInPeriod') :
                  filter === 'all' ? t('tasks.noTasks') :
                  filter === 'pending' ? t('tasks.noPending') :
                  t('tasks.noCompleted')
                }
              </p>
            </div>
          )}
        </div>
      </div>

      {showCreateModal && (
        <CreateTaskModal
          listId={listId}
          task={editingTask}
          onClose={() => {
            setShowCreateModal(false);
            setEditingTask(null);
          }}
          onCreated={() => {
            fetchData();
            setShowCreateModal(false);
            setEditingTask(null);
          }}
        />
      )}

      {showShareModal && (
        <ShareModal
          listId={listId}
          type="task"
          onClose={() => setShowShareModal(false)}
          onShared={() => {
            fetchData();
            setShowShareModal(false);
          }}
        />
      )}

      {showConfirmModal && confirmAction && (
        <ConfirmModal
          title={confirmAction.title}
          message={confirmAction.message}
          onConfirm={confirmModalAction}
          onCancel={() => {
            setShowConfirmModal(false);
            setConfirmAction(null);
          }}
        />
      )}

      {/* Task Detail Modal */}
      {viewingTask && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-zinc-800 border-2 border-[#18181B] dark:border-zinc-600 rounded-2xl hard-shadow w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className={`w-12 h-12 rounded-xl border-2 border-[#18181B] dark:border-zinc-600 ${getPriorityColor(viewingTask.priority)} flex items-center justify-center font-mono font-bold text-lg`}>
                    {viewingTask.priority}
                  </div>
                  <div>
                    <h2 className={`text-xl font-bold ${viewingTask.completed ? 'line-through text-zinc-400 dark:text-zinc-500' : 'text-zinc-900 dark:text-white'}`}>
                      {viewingTask.title}
                    </h2>
                    <span className={`text-sm ${viewingTask.completed ? 'text-green-600 dark:text-green-400' : 'text-zinc-500 dark:text-zinc-400'}`}>
                      {viewingTask.completed ? t('tasks.completedFilter') : t('tasks.pendingFilter')}
                    </span>
                  </div>
                </div>
                <button
                  onClick={() => setViewingTask(null)}
                  className="p-2 hover:bg-zinc-100 dark:hover:bg-zinc-700 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5 text-zinc-900 dark:text-white" />
                </button>
              </div>

              <div className="space-y-4">
                {/* Points */}
                <div className="flex items-center gap-2">
                  <span className="text-sm font-bold text-zinc-500 dark:text-zinc-400">{t('modals.rewardPoints')}</span>
                  <span className="px-3 py-1 bg-[#FFC247]/20 text-[#18181B] dark:text-[#FFC247] rounded-lg font-mono font-bold">
                    +{viewingTask.points} {t('tasks.pts')}
                  </span>
                </div>

                {/* Due date */}
                {viewingTask.due_date && (
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-zinc-500 dark:text-zinc-400" />
                    <span className="text-sm font-bold text-zinc-500 dark:text-zinc-400">{t('modals.dueDate')}:</span>
                    <span className="text-sm text-zinc-900 dark:text-white">
                      {new Date(viewingTask.due_date).toLocaleDateString(getLocale(), { 
                        day: 'numeric', 
                        month: 'long', 
                        year: 'numeric' 
                      })}
                    </span>
                  </div>
                )}

                {/* Recurrent */}
                {viewingTask.recurrent && (
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <Repeat className="w-4 h-4 text-[#FF8BA7]" />
                      <span className="text-sm font-bold text-zinc-500 dark:text-zinc-400">{t('tasks.recurrent')}:</span>
                      <span className="text-sm text-zinc-900 dark:text-white">
                        {viewingTask.recurrence_days} {t('modals.recurrenceDays')}
                      </span>
                    </div>
                    {viewingTask.available_from && (
                      <div className="flex items-center gap-2 ml-6 text-sm">
                        <Clock className="w-4 h-4 text-[#FF8BA7]" />
                        <span className="font-bold text-zinc-500 dark:text-zinc-400">{t('tasks.availableFrom')}:</span>
                        <span className={new Date(viewingTask.available_from) > new Date() ? 'text-orange-600 dark:text-orange-400' : 'text-green-600 dark:text-green-400'}>
                          {new Date(viewingTask.available_from).toLocaleDateString(getLocale(), { 
                            day: 'numeric', 
                            month: 'long', 
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                          {new Date(viewingTask.available_from) > new Date() && (
                            <span className="ml-2 text-xs text-orange-500 dark:text-orange-400">({t('tasks.notYetAvailable')})</span>
                          )}
                        </span>
                      </div>
                    )}
                  </div>
                )}

                {/* Tags */}
                {viewingTask.tags && viewingTask.tags.length > 0 && (
                  <div>
                    <span className="text-sm font-bold text-zinc-500 dark:text-zinc-400 block mb-2">{t('modals.tags')}:</span>
                    <div className="flex flex-wrap gap-2">
                      {viewingTask.tags.map((tag, idx) => (
                        <span key={idx} className="px-2 py-1 bg-[#ADE498]/30 dark:bg-[#ADE498]/20 text-[#18181B] dark:text-[#ADE498] rounded-lg text-sm flex items-center gap-1">
                          <Tag className="w-3 h-3" />
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Completed at */}
                {viewingTask.completed_at && (
                  <div className="flex items-center gap-2 pt-2 border-t border-zinc-100 dark:border-zinc-700">
                    <Check className="w-4 h-4 text-green-600 dark:text-green-400" />
                    <span className="text-sm font-bold text-zinc-500 dark:text-zinc-400">{t('tasks.filterByCompleted')}:</span>
                    <span className="text-sm text-zinc-900 dark:text-white">
                      {new Date(viewingTask.completed_at).toLocaleDateString(getLocale(), { 
                        day: 'numeric', 
                        month: 'long', 
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </span>
                  </div>
                )}

                {/* Created at */}
                <div className="flex items-center gap-2 text-zinc-400 dark:text-zinc-500 text-xs">
                  <Clock className="w-3 h-3" />
                  <span>{t('tasks.filterByCreated')}:</span>
                  <span>
                    {new Date(viewingTask.created_at).toLocaleDateString(getLocale(), { 
                      day: 'numeric', 
                      month: 'long', 
                      year: 'numeric' 
                    })}
                  </span>
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-2 mt-6 pt-4 border-t border-zinc-100 dark:border-zinc-700">
                <button
                  onClick={() => {
                    handleCloneTask(viewingTask.task_id);
                    setViewingTask(null);
                  }}
                  className="flex-1 py-2 px-4 bg-[#ADE498] border-2 border-[#18181B] dark:border-zinc-600 rounded-lg font-bold text-sm text-[#18181B] hover:bg-[#bdeaa8] transition-colors flex items-center justify-center gap-2"
                >
                  <Copy className="w-4 h-4" />
                  {t('tasks.cloneTask')}
                </button>
                {!viewingTask.completed && (
                  <button
                    onClick={() => {
                      setEditingTask(viewingTask);
                      setViewingTask(null);
                      setShowCreateModal(true);
                    }}
                    className="flex-1 py-2 px-4 bg-zinc-100 dark:bg-zinc-700 border-2 border-[#18181B] dark:border-zinc-600 rounded-lg font-bold text-sm text-zinc-900 dark:text-white hover:bg-zinc-200 dark:hover:bg-zinc-600 transition-colors flex items-center justify-center gap-2"
                  >
                    <Edit className="w-4 h-4" />
                    {t('tasks.editTask')}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Users & Points Modal */}
      {showUsersModal && userPoints && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-zinc-800 border-2 border-[#18181B] dark:border-zinc-600 rounded-2xl hard-shadow w-full max-w-md max-h-[80vh] overflow-hidden">
            <div className="p-4 border-b border-zinc-200 dark:border-zinc-700 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Users className="w-5 h-5 text-zinc-900 dark:text-white" />
                <h2 className="text-lg font-bold text-zinc-900 dark:text-white">{t('tasks.teamMembers')}</h2>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => {
                    fetchDonationsHistory();
                    setShowDonationsHistory(!showDonationsHistory);
                  }}
                  className={`p-2 rounded-lg transition-colors ${showDonationsHistory ? 'bg-[#FF8BA7] text-white' : 'hover:bg-zinc-100 dark:hover:bg-zinc-700'}`}
                  title={t('donations.history')}
                >
                  <History className="w-5 h-5" />
                </button>
                <button
                  onClick={() => setShowUsersModal(false)}
                  className="p-2 hover:bg-zinc-100 dark:hover:bg-zinc-700 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5 text-zinc-900 dark:text-white" />
                </button>
              </div>
            </div>
            
            <div className="p-4 overflow-y-auto max-h-[60vh]">
              {!showDonationsHistory ? (
                <>
                  {/* Total Points Summary */}
                  <div className="mb-4 p-3 bg-zinc-50 dark:bg-zinc-700 rounded-xl text-center">
                    <span className="text-sm text-zinc-500 dark:text-zinc-400">{t('tasks.totalListPoints')}</span>
                    <div className="text-2xl font-bold text-zinc-900 dark:text-white">{userPoints.total_points} pts</div>
                  </div>
                  
                  {/* Users List */}
                  <div className="space-y-2">
                    {userPoints.users
                      .sort((a, b) => b.points - a.points)
                      .map((u, i) => (
                      <div
                        key={u.user_id || u.email}
                        className={`flex items-center gap-3 p-3 rounded-xl border-2 ${
                          u.user_id === user?.user_id 
                            ? 'bg-[#ADE498]/20 dark:bg-[#ADE498]/10 border-[#18181B] dark:border-zinc-500' 
                            : 'bg-white dark:bg-zinc-700 border-zinc-200 dark:border-zinc-600'
                        }`}
                      >
                        {/* Rank */}
                        <div className="w-6 h-6 flex items-center justify-center">
                          {i === 0 && u.points > 0 ? (
                            <Trophy className="w-5 h-5 text-yellow-500" />
                          ) : (
                            <span className="text-sm font-bold text-zinc-400 dark:text-zinc-500">#{i + 1}</span>
                          )}
                        </div>
                        
                        {/* Avatar */}
                        {u.picture ? (
                          <img src={u.picture} alt={u.name} className="w-10 h-10 rounded-full border-2 border-zinc-200 dark:border-zinc-600" />
                        ) : (
                          <div className="w-10 h-10 rounded-full bg-zinc-200 dark:bg-zinc-600 flex items-center justify-center text-lg font-bold border-2 border-zinc-300 dark:border-zinc-500 text-zinc-900 dark:text-white">
                            {(u.name || u.email)[0].toUpperCase()}
                          </div>
                        )}
                        
                        {/* Name & Role */}
                        <div className="flex-1 min-w-0">
                          <div className="font-bold truncate text-zinc-900 dark:text-white">
                            {u.user_id === user?.user_id ? t('tasks.you') : (u.name || u.email.split('@')[0])}
                          </div>
                          <div className="text-xs text-zinc-500 dark:text-zinc-400 truncate">
                            {u.is_owner ? t('tasks.owner') : u.email}
                          </div>
                        </div>
                        
                        {/* Points & Action Buttons */}
                        <div className="flex items-center gap-2">
                          <div className={`text-right ${u.points > 0 ? 'text-green-600 dark:text-green-400' : 'text-zinc-400 dark:text-zinc-500'}`}>
                            <div className="text-lg font-bold">{u.points}</div>
                            <div className="text-xs">pts</div>
                          </div>
                          
                          {/* Action buttons - show for other users */}
                          {u.user_id !== user?.user_id && (
                            <div className="flex items-center gap-1">
                              {/* Donate button */}
                              <button
                                onClick={() => u.user_id && getMaxDonation() > 0 && openDonateModal(u)}
                                disabled={!u.user_id || getMaxDonation() <= 0}
                                className={`p-2 rounded-lg border-2 border-[#18181B] dark:border-zinc-600 transition-all ${
                                  u.user_id && getMaxDonation() > 0 
                                    ? 'bg-[#FF8BA7] hover:bg-[#ff7a9a] cursor-pointer' 
                                    : 'bg-zinc-200 dark:bg-zinc-600 cursor-not-allowed opacity-50'
                                }`}
                                title={!u.user_id ? t('donations.notRegistered') : (getMaxDonation() > 0 ? t('donations.donate') : t('donations.noPoints'))}
                                data-testid={`donate-btn-${u.user_id || u.email}`}
                              >
                                <Gift className={`w-4 h-4 ${u.user_id && getMaxDonation() > 0 ? 'text-[#18181B]' : 'text-zinc-400 dark:text-zinc-500'}`} />
                              </button>
                              
                              {/* Deduct points button - only for owner */}
                              {isOwner && u.user_id && (
                                <button
                                  onClick={() => u.points > 0 && openDeductModal(u)}
                                  disabled={u.points <= 0}
                                  className={`p-2 rounded-lg border-2 border-[#18181B] dark:border-zinc-600 transition-all ${
                                    u.points > 0 
                                      ? 'bg-red-400 hover:bg-red-500 cursor-pointer' 
                                      : 'bg-zinc-200 dark:bg-zinc-600 cursor-not-allowed opacity-50'
                                  }`}
                                  title={u.points > 0 ? t('deductions.deduct') : t('deductions.noPoints')}
                                  data-testid={`deduct-btn-${u.user_id}`}
                                >
                                  <MinusCircle className={`w-4 h-4 ${u.points > 0 ? 'text-[#18181B]' : 'text-zinc-400 dark:text-zinc-500'}`} />
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                  
                </>
              ) : (
                /* Donations History */
                <div>
                  <h3 className="font-bold text-zinc-900 dark:text-white mb-3 flex items-center gap-2">
                    <Gift className="w-4 h-4 text-[#FF8BA7]" />
                    {t('donations.history')}
                  </h3>
                  
                  {donations.length === 0 ? (
                    <div className="text-center py-8 text-zinc-500 dark:text-zinc-400">
                      <Gift className="w-12 h-12 mx-auto mb-2 opacity-30" />
                      <p>{t('donations.noHistory')}</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {donations.map((d) => (
                        <div key={d.donation_id} className="p-3 bg-zinc-50 dark:bg-zinc-700 rounded-xl">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <span className="font-bold text-zinc-900 dark:text-white">{d.donor_name}</span>
                              <span className="text-zinc-400">→</span>
                              <span className="font-bold text-zinc-900 dark:text-white">{d.recipient_name}</span>
                            </div>
                            <span className="text-[#FF8BA7] font-bold">+{d.points} pts</span>
                          </div>
                          <div className="text-xs text-zinc-500 dark:text-zinc-400 mt-1">
                            {new Date(d.created_at).toLocaleDateString(getLocale(), { 
                              day: 'numeric', 
                              month: 'short', 
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Donate Modal */}
      {showDonateModal && donationRecipient && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white dark:bg-zinc-800 border-2 border-[#18181B] dark:border-zinc-600 rounded-2xl hard-shadow w-full max-w-sm p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold text-zinc-900 dark:text-white flex items-center gap-2">
                <Gift className="w-5 h-5 text-[#FF8BA7]" />
                {t('donations.donateTitle')}
              </h2>
              <button
                onClick={() => {
                  setShowDonateModal(false);
                  setDonationRecipient(null);
                }}
                className="p-2 hover:bg-zinc-100 dark:hover:bg-zinc-700 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-zinc-900 dark:text-white" />
              </button>
            </div>
            
            {/* Recipient info */}
            <div className="flex items-center gap-3 p-3 bg-zinc-50 dark:bg-zinc-700 rounded-xl mb-4">
              {donationRecipient.picture ? (
                <img src={donationRecipient.picture} alt={donationRecipient.name} className="w-12 h-12 rounded-full border-2 border-zinc-200 dark:border-zinc-600" />
              ) : (
                <div className="w-12 h-12 rounded-full bg-zinc-200 dark:bg-zinc-600 flex items-center justify-center text-xl font-bold border-2 border-zinc-300 dark:border-zinc-500 text-zinc-900 dark:text-white">
                  {(donationRecipient.name || donationRecipient.email)[0].toUpperCase()}
                </div>
              )}
              <div>
                <div className="font-bold text-zinc-900 dark:text-white">{donationRecipient.name}</div>
                <div className="text-sm text-zinc-500 dark:text-zinc-400">{donationRecipient.email}</div>
              </div>
            </div>
            
            {/* Amount selector */}
            <div className="mb-4">
              <label className="block text-sm font-bold mb-2 text-zinc-900 dark:text-white">
                {t('donations.amount')}
              </label>
              <div className="flex items-center gap-2">
                <input
                  type="range"
                  min="1"
                  max={getMaxDonation()}
                  value={donationAmount}
                  onChange={(e) => setDonationAmount(parseInt(e.target.value))}
                  className="flex-1 h-2 bg-zinc-200 dark:bg-zinc-600 rounded-lg appearance-none cursor-pointer accent-[#FF8BA7]"
                />
                <div className="w-16 text-center">
                  <span className="text-2xl font-bold text-[#FF8BA7]">{donationAmount}</span>
                  <span className="text-sm text-zinc-500 dark:text-zinc-400 ml-1">pts</span>
                </div>
              </div>
              <p className="text-xs text-zinc-500 dark:text-zinc-400 mt-2">
                {t('donations.yourBalance')}: {getMyPoints()} pts • Max: {getMaxDonation()} pts
              </p>
            </div>
            
            {/* Action buttons */}
            <div className="flex gap-2">
              <button
                onClick={() => {
                  setShowDonateModal(false);
                  setDonationRecipient(null);
                }}
                className="flex-1 rounded-full border-2 border-[#18181B] dark:border-zinc-600 bg-white dark:bg-zinc-700 text-zinc-900 dark:text-white font-bold py-2.5 hover:bg-zinc-100 dark:hover:bg-zinc-600 transition-all"
              >
                {t('modals.cancel')}
              </button>
              <button
                onClick={handleDonate}
                disabled={donationLoading || donationAmount < 1}
                className="flex-1 rounded-full border-2 border-[#18181B] bg-[#FF8BA7] text-[#18181B] font-bold py-2.5 hover:bg-[#ff7a9a] transition-all disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {donationLoading ? (
                  <div className="w-5 h-5 border-2 border-[#18181B] border-t-transparent rounded-full animate-spin" />
                ) : (
                  <>
                    <Gift className="w-4 h-4" />
                    {t('donations.donate')}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Deduct Points Modal (Owner only) */}
      {showDeductModal && deductTarget && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white dark:bg-zinc-800 border-2 border-[#18181B] dark:border-zinc-600 rounded-2xl hard-shadow w-full max-w-sm p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold text-zinc-900 dark:text-white flex items-center gap-2">
                <MinusCircle className="w-5 h-5 text-red-500" />
                {t('deductions.deductTitle')}
              </h2>
              <button
                onClick={() => {
                  setShowDeductModal(false);
                  setDeductTarget(null);
                }}
                className="p-2 hover:bg-zinc-100 dark:hover:bg-zinc-700 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-zinc-900 dark:text-white" />
              </button>
            </div>
            
            {/* Target user info */}
            <div className="flex items-center gap-3 p-3 bg-zinc-50 dark:bg-zinc-700 rounded-xl mb-4">
              {deductTarget.picture ? (
                <img src={deductTarget.picture} alt={deductTarget.name} className="w-12 h-12 rounded-full border-2 border-zinc-200 dark:border-zinc-600" />
              ) : (
                <div className="w-12 h-12 rounded-full bg-zinc-200 dark:bg-zinc-600 flex items-center justify-center text-xl font-bold border-2 border-zinc-300 dark:border-zinc-500 text-zinc-900 dark:text-white">
                  {(deductTarget.name || deductTarget.email)[0].toUpperCase()}
                </div>
              )}
              <div>
                <div className="font-bold text-zinc-900 dark:text-white">{deductTarget.name}</div>
                <div className="text-sm text-zinc-500 dark:text-zinc-400">{deductTarget.email}</div>
              </div>
            </div>
            
            {/* Amount selector */}
            <div className="mb-4">
              <label className="block text-sm font-bold mb-2 text-zinc-900 dark:text-white">
                {t('deductions.amount')}
              </label>
              <div className="flex items-center gap-2">
                <input
                  type="range"
                  min="1"
                  max={getTargetPoints()}
                  value={deductAmount}
                  onChange={(e) => setDeductAmount(parseInt(e.target.value))}
                  className="flex-1 h-2 bg-zinc-200 dark:bg-zinc-600 rounded-lg appearance-none cursor-pointer accent-red-500"
                />
                <div className="w-16 text-center">
                  <span className="text-2xl font-bold text-red-500">{deductAmount}</span>
                  <span className="text-sm text-zinc-500 dark:text-zinc-400 ml-1">pts</span>
                </div>
              </div>
              <p className="text-xs text-zinc-500 dark:text-zinc-400 mt-2">
                {t('deductions.userBalance')}: {getTargetPoints()} pts
              </p>
            </div>
            
            {/* Reason input */}
            <div className="mb-4">
              <label className="block text-sm font-bold mb-2 text-zinc-900 dark:text-white">
                {t('deductions.reason')}
              </label>
              <input
                type="text"
                value={deductReason}
                onChange={(e) => setDeductReason(e.target.value)}
                placeholder={t('deductions.reasonPlaceholder')}
                className="w-full px-3 py-2 rounded-lg border-2 border-zinc-200 dark:border-zinc-600 bg-white dark:bg-zinc-700 text-zinc-900 dark:text-white placeholder-zinc-400 focus:outline-none focus:border-red-400"
              />
            </div>
            
            {/* Action buttons */}
            <div className="flex gap-2">
              <button
                onClick={() => {
                  setShowDeductModal(false);
                  setDeductTarget(null);
                }}
                className="flex-1 rounded-full border-2 border-[#18181B] dark:border-zinc-600 bg-white dark:bg-zinc-700 text-zinc-900 dark:text-white font-bold py-2.5 hover:bg-zinc-100 dark:hover:bg-zinc-600 transition-all"
              >
                {t('modals.cancel')}
              </button>
              <button
                onClick={handleDeductPoints}
                disabled={deductLoading || deductAmount < 1}
                className="flex-1 rounded-full border-2 border-[#18181B] bg-red-500 text-white font-bold py-2.5 hover:bg-red-600 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {deductLoading ? (
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <>
                    <MinusCircle className="w-4 h-4" />
                    {t('deductions.deduct')}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
